<template>
  <div>
    <PageAllow controller="ReturnOrders" action="index" />
    <Breadcrumbs :items="items" />

    <v-sheet class="transparent-background-color">
      <v-row dense class="mx-5">
        <v-col cols="12" sm="12" md="4">
          <v-row dense align="center" class="pa-1 pe-2">
            <SearcherDatetimeRangeWithRadio
              v-model="options"
              type="datetime"
              show-radios
              :radios="datetimeRadios"
              removeIcon
            />
          </v-row>
        </v-col>
        <v-col cols="12" sm="12" md="8" align-self="center">
          <v-row dense align="center" class="search-controls">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="px-0 py-0 caption font-weight-bold grey--text"
                  color="primary"
                  @click.stop="onSearch()"
                >
                  <v-icon small class="pe-1">fa-search</v-icon>
                  {{ $t("Search.search") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.searchTip") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="caption font-weight-bold grey--text"
                  @click.stop="resetSearch()"
                >
                  <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                  {{ $t("Search.reset") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.resetTip") }}</span>
            </v-tooltip>
            <v-divider vertical />
            <v-btn
              class="caption font-weight-bold grey--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
              small
              text
              @click.stop="$store.commit('toggleSearchPanel')"
            >
              {{ $t("Search.pro") }}
              <v-icon small class="ps-1">{{
                $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
              }}</v-icon>
            </v-btn>
            <v-spacer />
            <v-btn
              v-if="
                !$route.path.startsWith('/return-orders-') &&
                $permission.permissionAllow('ReturnOrders', 'add')
              "
              small
              color="primary"
              href="/#/return-order-new"
              class="ms-1"
            >
              <v-icon x-small class="pe-1">fa-plus</v-icon>
              {{ $t("CRUD.newInfo", [$t("ReturnOrder.ObjectName")]) }}
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
      <v-expansion-panels
        v-model="$store.getters.panels"
        multiple
        flat
        accordion
      >
        <v-expansion-panel class="transparent-background-color">
          <v-expansion-panel-content>
            <v-row dense align="center">
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="order_code"
                  v-model="options.order_code"
                  @text="orderCodeKeyword = $event"
                  :label="$t('ReturnOrder.Fields.order_code')"
                />
              </v-col>
              <v-col cols="12" class="d-flex mt-2">
                <SearcherStatus
                  v-model="options.status"
                  :items="$consts.return_order.status"
                  :label="$t('ReturnOrder.status.title')"
                />
                <v-spacer />
                <SearcherStatus
                  v-model="options.pay_flgs"
                  :items="$consts.bill.pay_flgs"
                  :label="$t('Bill.payFlgs.title')"
                />
                <v-spacer />
                <SearcherCancelFlgs v-model="options.cancel_flgs" />
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <div class="mx-4 mb-4">
        <v-data-table
          :headers="headers"
          :items="returnOrders"
          :options.sync="options"
          :page.sync="paging.page"
          :server-items-length="paging.count"
          :loading="loading"
          mobile-breakpoint="800"
          multi-sort
          class="elevation-2 px-4 pt-1 sticky-table"
          id="sticky-table"
          :class="stickTableCols ? 'sticky-first-col sticky-last-col' : ''"
          fixed-header
          :footer-props="$t('$config.footer-props')"
        >
          <template v-slot:header.shop>
            {{ $consts.shopName() }}
          </template>
          <template v-slot:header.actions="{ header }">
            {{ header.text }}
            <v-icon x-small>fa-sliders-h</v-icon>
          </template>

          <template v-slot:item.order_code="{ item, index }">
            <span
              class="text-no-wrap caption font-weight-bold"
              :v-resize="!index ? $stickTableResizer() : null"
            >
              {{ item.order_code }}
            </span>
            <br />
            <span
              class="text-no-wrap caption secondary--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
            >
              {{ item.created | formatTime }}
            </span>
          </template>
          <template v-slot:item.shop.shop_name="{ item }">
            <span class="text-no-wrap">
              {{ item.shop.shop_name }}
            </span>
            <br />
            <span class="text-no-wrap caption secondary--text">
              {{ item.shop.shop_code }}
            </span>
          </template>
          <template v-slot:item.check_price="{ item }">
            {{ $t("Global.money") }}{{ item.check_price | formatMoney }}
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip
              small
              label
              :color="item.status | returnOrderStatusColor"
              :text-color="item.status | returnOrderStatusTextColor"
              >{{ item.status | returnOrderStatusText }}
            </v-chip>
            <br />
            <v-chip
              v-if="item.cancel_flg || item.error_flg"
              x-small
              label
              outlined
              color="danger"
            >
              {{
                item.cancel_flg
                  ? $t("CRUD.cancelled")
                  : $t("Order.redeliverError")
              }}
            </v-chip>
          </template>
          <template v-slot:item.bill.pay_flg="{ item }">
            <span class="text-no-wrap">
              <v-chip
                small
                label
                :color="item.bill.pay_flg | billPayFlgsColor"
                :text-color="item.bill.pay_flg | billPayFlgsTextColor"
                >{{ item.bill.pay_flg | billPayFlgsText }}
              </v-chip>
              <v-chip
                small
                label
                class="d-none ms-1"
                :color="item.bill.status | billStatusColor"
                :text-color="item.bill.status | billStatusTextColor"
                >{{ item.bill.status | billStatusText }}
              </v-chip>
            </span>
          </template>
          <template v-slot:item.origin_flg="{ item }">
            <v-chip
              small
              label
              :color="item.origin_flg | orderOriginFlgsColor"
              :text-color="item.origin_flg | orderOriginFlgsTextColor"
              >{{ item.origin_flg | orderOriginFlgsText }}
            </v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <OrderActions
              :order="item"
              showView
              @done="getOrdersFromAPI()"
            />
          </template>
        </v-data-table>
      </div>
    </v-sheet>
  </div>
</template>

<script>
import { mapState } from "vuex";
import i18n from "@/i18n";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import SearcherDatetimeRangeWithRadio from "@/components/plugins/SearcherDatetimeRangeWithRadio";
import SearcherKeyword from "@/components/plugins/SearcherKeyword";
import SearcherStatus from "@/components/plugins/SearcherStatus";
import SearcherCancelFlgs from "@/components/plugins/SearcherCancelFlgs";
import OrderActions from "./plugins/Actions";
import ReturnOrders from "@/api/return_orders";

const defaultOptions = {
  datetimeRadioSelected: "created",
  created_time_from:
    new Date(new Date().getTime() - 30 * 24 * 3600 * 1000)
      .toISOString()
      .substr(0, 10) + " 00:00:00",
  created_time_to: new Date().toISOString().substr(0, 10) + " 23:59:59",
  actual_time_from: "",
  actual_time_to: "",
  order_code: "",
  origin_flgs: [],
  status: [1, 2, 3, 4],
  pay_flgs: [1, 2, 3],
  cancel_flgs: [0],
  sortBy: [],
  sortDesc: [],
};

export default {
  components: {
    PageAllow,
    Breadcrumbs,
    SearcherDatetimeRangeWithRadio,
    SearcherKeyword,
    SearcherStatus,
    SearcherCancelFlgs,
    OrderActions,
  },

  computed: {
    items() {
      let items = [
        {
          text: i18n.t("Nav.home"),
          disabled: false,
          href: "/home",
        },
        {
          text: i18n.t("Nav.returnOrderManagement"),
          disabled: false,
          replace: true,
          href: "/return-orders",
        },
        {
          text: i18n.t("Nav.returnOrders"),
          disabled: true,
          href: "/return-orders",
        }
      ];
      return items;
    },
    datetimeRadios: () => [
      {
        key: "created",
        text: i18n.t("ReturnOrder.Fields.created_time"),
      },
      {
        key: "actual",
        text: i18n.t("ReturnOrder.Fields.actual_time"),
      },
    ],
    headers() {
      return [
        {
          text:
            i18n.t("ReturnOrder.Fields.order_code") +
            " / " +
            i18n.t("ReturnOrder.Fields.created_time"),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "order_code",
        },
        {
          text: i18n.t("ReturnOrder.Fields.shop", [this.$consts.shopName()]),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "shop.shop_name",
        },
        {
          text: i18n.t("ReturnOrder.Fields.check_price"),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "check_price",
        },
        {
          text: i18n.t("ReturnOrder.Fields.memo"),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "memo",
        },
        {
          text: i18n.t("ReturnOrder.Fields.status"),
          align: "center",
          class: "text-no-wrap",
          sortable: false,
          value: "status",
        },
        {
          text: i18n.t("ReturnOrder.Fields.bill_pay_flgs"),
          // + " / " + i18n.t("Order.Fields.bill_status"),
          align: "center",
          class: "text-no-wrap",
          sortable: false,
          value: "bill.pay_flg",
        },
        {
          text: i18n.t("TableHeaders.actions"),
          align: "start",
          sortable: false,
          value: "actions",
        },
      ];
    },
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading", "settings", "stickTableCols"]),
  },

  mounted() {
    this.$commonUtils
      .loadOptions("return-orders", [])
      .then(
        function (data) {
          if (data.options) {
            this.options = data.options;
          } else this.resetSearch();
        }.bind(this)
      );
  },

  data: () => ({
    orderCodeKeyword: "",
    options: JSON.parse(
      sessionStorage.getItem("return-orders-options")
        ? sessionStorage.getItem("return-orders-options")
        : JSON.stringify(defaultOptions)
    ),
    paging: {},
    returnOrders: [],
  }),

  watch: {
    $route() {
      this.getOrdersFromAPI();
    },

    options: {
      // immediate: true, //为避免覆盖session里保存的options，不能启用immediate
      deep: true,
      handler() {
        this.getOrdersFromAPI();
        sessionStorage.setItem(
          "return-orders-options",
          JSON.stringify(this.options)
        );
      },
    },
  },

  methods: {
    resetSearch() {
      this.options = JSON.parse(JSON.stringify(defaultOptions));
      [this.$refs.order_code].forEach((k) => {
        if (k) k.clear();
      });
    },
    onSearch() {
      this.options = Object.assign({}, this.options, {
        order_code: this.orderCodeKeyword,
      });
    },
    getOrdersFromAPI() {
      var options = JSON.parse(JSON.stringify(this.options));
      ReturnOrders.fetch(options).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.returnOrders = response.data.return_orders;
            this.paging = response.data.paging;
          } else if (response.data.error.code.isNoResultSet()) {
            this.options.page = 1;
          }
        }.bind(this)
      );
    },
  },
};
</script>
